import { default as cloneDeep } from 'lodash/cloneDeep';
import { default as isEqual } from 'lodash/isEqual';
import { ChangeEvent, Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { rem, Text, Flex, Divider, Button, IconButton, Input, InputGroup } from '@vimeo/bokeh';
import { TrashBin, Star, StarFilled } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { Optional } from '@vimeo/core/types/generic';
import { BokehTooltip } from '@vimeo/live-core/components/bokeh';
import { BackButtonPanel } from '@vimeo/live-core/components/panel';
import { interactionToolsConfig } from '@vimeo/live-core/configs/InteractionToolsConfig';
import {
  trackCancelPollCreation,
  trackSavePoll,
} from '@vimeo/live-core/tracking/tracking_engagement_polls';
import { T_CANCEL } from '@vimeo/live-core/translations';
import { IPoll, IPollOption } from '@vimeo/live-core/types/interaction_tools';
import { createDomName } from '@vimeo/live-core/utils/dom/identifying';

import { translations } from '../../../../translations';
import { PollQuestionField } from './PollQuestionField';
function isFormValid(poll: IPoll): boolean {
  if (!poll) {
    return false;
  }

  const filteredOptions = poll.options.filter(
    (option: IPollOption) =>
      option.text.trim().length > 0 &&
      option.text.trim().length <= interactionToolsConfig.POLLS.MAX_OPTION_LENGTH,
  );

  const validOptions =
    filteredOptions.length >= 2 && filteredOptions.length === poll.options.length;

  const validQuestion =
    poll.question.trim().length > 0 &&
    poll.question.trim().length <= interactionToolsConfig.POLLS.MAX_QUESTION_LENGTH;

  return validQuestion && validOptions;
}

interface IPollEditProps extends IBaseDomElementProps {
  poll: IPoll;
  onClose: Dispatch<SetStateAction<boolean>>;
  onSubmit(poll: IPoll): Promise<void>;
}

export function PollForm({
  id = createDomName('poll-form'),
  className = createDomName('poll-form'),
  poll,
  onClose,
  onSubmit,
}: IPollEditProps) {
  const [editablePoll, setEditablePoll] = useState<IPoll>(() => cloneDeep(poll));
  const [showError, setShowError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isQuestionValid, setIsQuestionValid] = useState<boolean>(true);

  const { options, question } = editablePoll;

  const hasChanges = useMemo(() => !isEqual(editablePoll, poll), [editablePoll, poll]);

  const onCloseClick = useCallback(() => {
    onClose(false);

    // If just creating and cancel.
    if (!editablePoll.id) {
      trackCancelPollCreation();
    }
  }, [onClose, editablePoll]);

  const onSubmitClick = useCallback(() => {
    setIsSaving(true);
    if (isFormValid(editablePoll)) {
      setShowError(false);
      if (!editablePoll.id) {
        editablePoll.id = uuid();
      }
      onSubmit(editablePoll);
      onClose(false);
      trackSavePoll(options.length);
    } else {
      setShowError(true);
    }
    setIsSaving(false);
  }, [onSubmit, editablePoll, onClose, options.length]);

  const addOptionClick = useCallback(
    () =>
      setEditablePoll({
        ...editablePoll,
        options: [
          ...editablePoll.options,
          {
            id: uuid(),
            text: '',
            correct: false,
          } as IPollOption,
        ],
      }),
    [editablePoll],
  );

  const onQuestionChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLTextAreaElement>) => {
      setEditablePoll({
        ...editablePoll,
        question: value,
      });
    },
    [editablePoll],
  );

  const onDeleteOptionClick = useCallback(
    (optionToRemove: IPollOption) => () => {
      setEditablePoll({
        ...editablePoll,
        options: editablePoll.options.filter((opt) => opt !== optionToRemove),
      });
    },
    [editablePoll],
  );

  const onCorrectCheckClick = useCallback(
    (checkedOption: IPollOption) => () => {
      setEditablePoll({
        ...editablePoll,
        options: editablePoll.options.map((opt) => {
          opt.correct = opt === checkedOption && !checkedOption.correct;
          return opt;
        }),
      });
    },
    [editablePoll],
  );

  const onOptionChange = useCallback(
    (changedOption: IPollOption, newText: string) => {
      if (newText.length > interactionToolsConfig.POLLS.MAX_OPTION_LENGTH) {
        return;
      }

      setEditablePoll({
        ...editablePoll,
        options: editablePoll.options.map((it) => {
          if (it === changedOption) {
            it.text = newText;
          }
          return it;
        }),
      });
    },
    [editablePoll],
  );

  return (
    <Flex id={id} className={className} grow={1} direction={'column'} gap={rem(16)}>
      <Flex>
        <BackButtonPanel
          id={createDomName(id, 'back-button')}
          className={createDomName(className, 'back-button')}
          title={poll.id ? translations.editPoll : translations.createPoll}
          onClick={onCloseClick}
        />
      </Flex>

      <Flex direction={'column'} gap={rem(8)}>
        <PollQuestionField
          hasSubmitError={showError}
          question={question}
          onQuestionChange={onQuestionChange}
          onValidate={setIsQuestionValid}
        />

        {options.map((option, index) => {
          const isInvalid: boolean = Boolean(showError && !option.text.trim().length);
          const message: Optional<string> = isInvalid ? translations.pleaseEnterAnOption : null;

          return (
            <Flex key={index} position={'relative'} alignItems={'flex-start'} paddingTop={rem(8)}>
              <InputGroup display={'flex'} flexDirection={'column'} gap={rem(8)}>
                <Text
                  className={createDomName(className, 'option-text')}
                  variant="body-xl"
                  fontSize={'text-sm'}
                >
                  {translations.numberedOption(index + 1)}
                </Text>

                <Input
                  className={createDomName(className, 'option-input')}
                  flexGrow={1}
                  tabIndex={index + 2}
                  value={option.text}
                  isInvalid={isInvalid}
                  size={'sm'}
                  placeholder={translations.addOption}
                  onChange={({ target: { value: newText } }) => onOptionChange(option, newText)}
                />

                {message ? (
                  <Text
                    className={createDomName(className, 'error-message')}
                    variant="body-xl"
                    color={'status-destructive-primary'}
                    fontSize={'text-xs'}
                    marginBottom={rem(12)}
                  >
                    {message}
                  </Text>
                ) : null}
              </InputGroup>

              <Flex position={'absolute'} top={rem(8)} right={0} gap={rem(4)}>
                <BokehTooltip label={translations.correctOption} shouldWrapChildren={false}>
                  <IconButton
                    className={createDomName(className, 'mark-option-button')}
                    aria-label={'correct'}
                    variant={'minimal'}
                    size={'xs'}
                    icon={option.correct ? <StarFilled /> : <Star />}
                    onClick={onCorrectCheckClick(option)}
                  />
                </BokehTooltip>

                <BokehTooltip label={translations.remove} shouldWrapChildren={false}>
                  <IconButton
                    className={createDomName(className, 'delete-option-button')}
                    aria-label={'remove'}
                    variant={'minimal'}
                    size={'xs'}
                    icon={<TrashBin />}
                    isDisabled={options.length <= 2}
                    onClick={onDeleteOptionClick(option)}
                  />
                </BokehTooltip>
              </Flex>
            </Flex>
          );
        })}

        <Flex direction={'column'} justifyContent={'space-between'} gap={rem(8)}>
          {options.length < interactionToolsConfig.POLLS.MAX_OPTION_COUNT ? (
            <Button
              id={createDomName(id, 'add-option-button')}
              className={createDomName(className, 'add-option-button')}
              variant={'secondary'}
              marginTop={rem(8)}
              size={'sm'}
              onClick={addOptionClick}
            >
              {translations.addOption}
            </Button>
          ) : null}

          <Divider marginY={rem(8)} borderColor={'stroke'} />

          <Button
            id={createDomName(id, 'save-button')}
            className={createDomName(className, 'save-button')}
            variant={'primary'}
            size={'sm'}
            isDisabled={!hasChanges || isSaving || !isQuestionValid}
            onClick={onSubmitClick}
          >
            {translations.save}
          </Button>

          <Button
            id={createDomName(id, 'cancel-button')}
            className={createDomName(className, 'cancel-button')}
            variant={'secondary'}
            size={'sm'}
            onClick={onCloseClick}
          >
            {T_CANCEL}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
}
